import {
  useAccessibility,
  hasModuleAccess,
  hasPermission,
  canAdd,
  canView,
  canEdit,
  canApprove,
  canReject,
  canCancel,
  getModulePermissions,
  hasRouteAccess,
  getModuleForRoute,
} from "./accessControl";

/**
 * Custom hook that provides access control functionality
 */
export const useAccessControl = () => {
  const accessibility = useAccessibility();

  return {
    // Get accessibility data
    accessibility,

    // Module access checks
    hasModuleAccess: (moduleName: string) =>
      hasModuleAccess(accessibility, moduleName),

    // Permission checks
    hasPermission: (
      moduleName: string,
      permission: "add" | "view" | "edit" | "approve" | "reject" | "cancel"
    ) => hasPermission(accessibility, moduleName, permission),

    // Specific permission checks
    canAdd: (moduleName: string) => canAdd(accessibility, moduleName),
    canView: (moduleName: string) => canView(accessibility, moduleName),
    canEdit: (moduleName: string) => canEdit(accessibility, moduleName),
    canApprove: (moduleName: string) => canApprove(accessibility, moduleName),
    canReject: (moduleName: string) => canReject(accessibility, moduleName),
    canCancel: (moduleName: string) => canCancel(accessibility, moduleName),

    // Get all permissions for a module
    getModulePermissions: (moduleName: string) =>
      getModulePermissions(accessibility, moduleName),

    // Route access checks
    hasRouteAccess: (routePath: string) =>
      hasRouteAccess(accessibility, routePath),
    getModuleForRoute: (routePath: string) =>
      getModuleForRoute(accessibility, routePath),

    // Utility functions
    getAccessibleModules: () => {
      if (!accessibility) return [];
      return Object.keys(accessibility).filter((moduleName) =>
        hasModuleAccess(accessibility, moduleName)
      );
    },

    getModulesWithPermission: (
      permission: "add" | "view" | "edit" | "approve" | "reject" | "cancel"
    ) => {
      if (!accessibility) return [];
      return Object.keys(accessibility).filter((moduleName) =>
        hasPermission(accessibility, moduleName, permission)
      );
    },

    // Check if user has any permission for a module
    hasAnyPermission: (moduleName: string) => {
      if (!accessibility || !moduleName) return false;
      const module = accessibility[moduleName];
      if (!module || !module.access) return false;

      return (
        module.add === 1 ||
        module.view === 1 ||
        module.edit === 1 ||
        module.approve === 1 ||
        module.reject === 1 ||
        module.cancel === 1
      );
    },

    // Get highest permission level for a module
    getHighestPermission: (moduleName: string) => {
      if (!accessibility || !moduleName) return null;
      const module = accessibility[moduleName];
      if (!module || !module.access) return null;

      if (module.add === 1) return "add";
      if (module.edit === 1) return "edit";
      if (module.approve === 1) return "approve";
      if (module.reject === 1) return "reject";
      if (module.cancel === 1) return "cancel";
      if (module.view === 1) return "view";

      return null;
    },
  };
};
